// Rateio Mensal de Honorários (Clockify F1)
// — Gera preview do rateio mensal por profissional usando horas Clockify.
// — Aplica criando lançamentos derivados em lancamentos_pagar (zera o original).
// — Lista alertas (divergências, sem horas, sem lançamento, categoria não mapeada).
import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Typography, Space, Flex, Alert, Tag, Table, Spin, App as AntApp,
  DatePicker, Modal, Statistic, Empty,
} from 'antd';
import {
  ReloadOutlined, CheckOutlined, CloseOutlined, WarningOutlined, EyeOutlined,
  CalculatorOutlined, FileDoneOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

function fmtMoney(v: number | string | null) {
  if (v === null || v === undefined) return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtHoras(seg: number) {
  if (!seg) return '0h';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

export default function RateioMensalClockify() {
  return <AntApp><Inner /></AntApp>;
}

function Inner() {
  const { message, modal } = AntApp.useApp();
  const [competencia, setCompetencia] = useState<string>(dayjs().startOf('month').format('YYYY-MM'));
  const [rateios, setRateios] = useState<any[]>([]);
  const [alertas, setAlertas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [gerandoPreview, setGerandoPreview] = useState(false);
  const [aplicandoId, setAplicandoId] = useState<number | null>(null);
  const [detalheRateio, setDetalheRateio] = useState<any | null>(null);
  const [dataInicioRateio, setDataInicioRateio] = useState<string | null>(null);

  // Carrega config Clockify (data de corte)
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'clockify_config_get' },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setDataInicioRateio(d?.data_inicio_rateio || null);
      } catch {}
    })();
  }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_rateio_listar', competencia },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setRateios(Array.isArray(d?.rateios) ? d.rateios : []);
      setAlertas(Array.isArray(d?.alertas) ? d.alertas : []);
    } catch (e: any) {
      message.error('Erro ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [competencia]);

  async function gerarPreview() {
    setGerandoPreview(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_rateio_preview', competencia },
        { headers: { Authorization: token }, timeout: 60_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.aviso) { message.warning(d.aviso); }
      else { message.success(`Preview gerado — ${d.previews?.length || 0} profissional(is) processado(s)`); }
      await carregar();
    } catch (e: any) {
      message.error('Erro ao gerar preview: ' + (e.response?.data?.error || e.message));
    } finally { setGerandoPreview(false); }
  }

  async function aplicar(rateioId: number, profNome: string) {
    modal.confirm({
      title: `Aplicar rateio de ${profNome}?`,
      content: 'Vai atualizar o lançamento original no Contas a Pagar populando o JSONB centros_custo com os splits (1 linha só). Reversível depois via "Desaplicar".',
      okText: 'Aplicar',
      okType: 'primary',
      cancelText: 'Cancelar',
      onOk: async () => {
        setAplicandoId(rateioId);
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'clockify_rateio_aplicar', id: rateioId },
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          message.success(`OK — ${d.splits_criados} split(s) no JSONB do lançamento`);
          await carregar();
        } catch (e: any) {
          message.error('Erro ao aplicar: ' + (e.response?.data?.error || e.message));
        } finally { setAplicandoId(null); }
      },
    });
  }

  async function desaplicar(rateioId: number, profNome: string) {
    modal.confirm({
      title: `Desaplicar rateio de ${profNome}?`,
      content: 'Restaura o centros_custo anterior do lançamento e marca o preview como "preview" novamente.',
      okText: 'Desaplicar', okType: 'danger', cancelText: 'Voltar',
      onOk: async () => {
        setAplicandoId(rateioId);
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'clockify_rateio_desaplicar', id: rateioId },
            { headers: { Authorization: token } });
          message.success('Rateio desaplicado — lançamento restaurado');
          await carregar();
        } catch (e: any) {
          message.error('Erro ao desaplicar: ' + (e.response?.data?.error || e.message));
        } finally { setAplicandoId(null); }
      },
    });
  }

  async function descartar(rateioId: number) {
    modal.confirm({
      title: 'Descartar preview?',
      content: 'O preview ficará marcado como descartado. Você pode gerar novamente.',
      okText: 'Descartar', okType: 'danger', cancelText: 'Voltar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'clockify_rateio_descartar', id: rateioId },
            { headers: { Authorization: token } });
          message.success('Descartado');
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  const previews = rateios.filter(r => r.status === 'preview');
  const aplicados = rateios.filter(r => r.status === 'aplicado');

  return (
    <div style={{ padding: '16px 24px' }}>
      <Title level={4} style={{ marginTop: 0, marginBottom: 4, color: '#020D7F' }}>
        ⏱️ Rateio Mensal de Honorários
      </Title>
      <Text type="secondary">
        Distribui o salário mensal de cada profissional entre os CCs proporcionalmente
        às horas registradas no Clockify. Horas em CCs não-projeto (Administração, Comercial Interno…)
        viram custo indireto na categoria DRE configurada.
      </Text>

      {!dataInicioRateio && (
        <Alert type="error" showIcon style={{ marginTop: 16, marginBottom: 12 }}
          message="Data de corte do rateio não configurada"
          description="Configure em Admin > Integrações > Clockify > Conexão. O rateio só funciona para competências A PARTIR dessa data — antes disso, lançamentos foram rateados manualmente pela Marina no CA e ficam intocáveis." />
      )}
      {dataInicioRateio && (
        <Alert type="info" showIcon style={{ marginTop: 16, marginBottom: 12 }}
          message={`Rateio automático ativo a partir de ${(() => {
            const s = String(dataInicioRateio).slice(0, 10);
            const [y, m, d] = s.split('-');
            return (y && m && d) ? `${d}/${m}/${y}` : s;
          })()}`}
          description="Lançamentos com competência anterior ficam intocados (já rateados manualmente no CA)." />
      )}

      <Card size="small" style={{ marginBottom: 12 }}>
        <Flex gap={12} align="center" wrap>
          <div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Competência</div>
            <DatePicker
              picker="month"
              value={dayjs(competencia)}
              onChange={(v) => v && setCompetencia(v.format('YYYY-MM'))}
              format="MM/YYYY"
              allowClear={false}
              style={{ width: 140 }}
              disabledDate={(d) => {
                if (!dataInicioRateio) return false;
                return d.endOf('month').isBefore(dayjs(dataInicioRateio));
              }}
            />
          </div>
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            loading={gerandoPreview}
            onClick={gerarPreview}
            disabled={!dataInicioRateio}
          >
            Gerar / Recalcular preview
          </Button>
          <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>
            Atualizar
          </Button>
          <Flex gap={20} style={{ marginLeft: 'auto' }}>
            <Statistic title="Em preview" value={previews.length} valueStyle={{ fontSize: 18 }} />
            <Statistic title="Aplicados" value={aplicados.length} valueStyle={{ fontSize: 18, color: '#52c41a' }} />
            <Statistic title="Alertas" value={alertas.filter(a => !a.resolvido).length} valueStyle={{ fontSize: 18, color: '#faad14' }} />
          </Flex>
        </Flex>
      </Card>

      {/* Alertas */}
      {alertas.length > 0 && (
        <Card size="small" style={{ marginBottom: 12 }} title={
          <Text strong style={{ color: '#B91626' }}>
            <WarningOutlined /> Alertas ({alertas.filter(a => !a.resolvido).length} ativos)
          </Text>
        }>
          <Table size="small" rowKey="id" dataSource={alertas} pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Tipo', dataIndex: 'tipo', key: 't', width: 180,
                render: (v: string) => {
                  const cor: Record<string, string> = {
                    valor_divergente: 'red', sem_horas: 'orange',
                    sem_lancamento: 'gold', categoria_nao_mapeada: 'volcano',
                    sem_clockify_user: 'magenta',
                  };
                  const label: Record<string, string> = {
                    valor_divergente: 'Valor divergente',
                    sem_horas: 'Sem horas',
                    sem_lancamento: 'Sem lançamento',
                    categoria_nao_mapeada: 'Categoria não mapeada',
                    sem_clockify_user: 'Sem user Clockify',
                  };
                  return <Tag color={cor[v] || 'default'}>{label[v] || v}</Tag>;
                } },
              { title: 'Profissional', dataIndex: 'profissional_nome', key: 'p' },
              { title: 'Detalhe', dataIndex: 'detalhe', key: 'd',
                render: (v: any) => <Text style={{ fontSize: 11, fontFamily: 'monospace' }}>
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </Text> },
              { title: 'Status', dataIndex: 'resolvido', key: 's', width: 100,
                render: (v: boolean) => v ? <Tag color="success">Resolvido</Tag> : <Tag color="warning">Ativo</Tag> },
            ] as ColumnsType<any>}
          />
        </Card>
      )}

      {/* Tabela de rateios */}
      <Card size="small" title={`Rateios — ${dayjs(competencia).format('MMMM/YYYY')}`}>
        {loading ? <Flex justify="center"><Spin /></Flex>
        : rateios.length === 0 ? (
          <Empty description={
            <div>
              <div>Nenhum rateio para essa competência.</div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                Clique em "Gerar / Recalcular preview" para calcular com base nas horas Clockify do mês.
              </div>
            </div>
          } />
        ) : (
          <Table size="small" rowKey="id" dataSource={rateios} pagination={{ pageSize: 50 }}
            columns={[
              { title: 'Profissional', dataIndex: 'profissional_nome', key: 'p', fixed: 'left',
                render: (v: string, r: any) => (
                  <div>
                    <div>{v}</div>
                    {r.parcelas_no_mes > 1 && (
                      <Tag color="purple" style={{ fontSize: 9, marginTop: 2 }}>
                        {r.parcelas_no_mes} parcelas no mês — total {fmtMoney(r.valor_total_mes)}
                      </Tag>
                    )}
                  </div>
                ) },
              { title: 'Valor origem', dataIndex: 'valor_origem', key: 'vo', align: 'right',
                render: (v: any) => fmtMoney(v) },
              { title: 'Salário base', dataIndex: 'valor_esperado', key: 've', align: 'right',
                render: (v: any, r: any) => (
                  <span style={{ color: r.divergente ? '#B91626' : 'inherit' }}>
                    {fmtMoney(v)}
                    {r.divergente && <Tag color="error" style={{ marginLeft: 4, fontSize: 9 }}>≠ base mês</Tag>}
                  </span>
                ) },
              { title: 'Horas total', dataIndex: 'horas_total_segundos', key: 'ht', align: 'right',
                render: (v: number) => fmtHoras(v) },
              { title: 'Projeto', dataIndex: 'horas_projeto_segundos', key: 'hp', align: 'right',
                render: (v: number) => <Tag color="blue">{fmtHoras(v)}</Tag> },
              { title: 'Não-projeto', dataIndex: 'horas_naoprojeto_segundos', key: 'hn', align: 'right',
                render: (v: number) => <Tag color="orange">{fmtHoras(v)}</Tag> },
              { title: 'Splits', dataIndex: 'splits', key: 'sp', align: 'center', width: 80,
                render: (v: any[]) => <Tag>{(v || []).length}</Tag> },
              { title: 'Status', dataIndex: 'status', key: 'st', width: 110,
                render: (v: string) => (
                  v === 'aplicado' ? <Tag color="success" icon={<CheckOutlined />}>Aplicado</Tag>
                  : v === 'descartado' ? <Tag color="default" icon={<CloseOutlined />}>Descartado</Tag>
                  : <Tag color="processing">Preview</Tag>
                ) },
              { title: '', key: 'a', fixed: 'right', width: 180,
                render: (_: any, r: any) => (
                  <Space size={4}>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => setDetalheRateio(r)} />
                    {r.status === 'preview' && (
                      <>
                        <Button size="small" type="primary" icon={<FileDoneOutlined />}
                          loading={aplicandoId === r.id}
                          onClick={() => aplicar(r.id, r.profissional_nome)}>
                          Aplicar
                        </Button>
                        <Button size="small" danger icon={<CloseOutlined />}
                          onClick={() => descartar(r.id)} />
                      </>
                    )}
                    {r.status === 'aplicado' && (
                      <Button size="small" danger
                        loading={aplicandoId === r.id}
                        onClick={() => desaplicar(r.id, r.profissional_nome)}>
                        Desaplicar
                      </Button>
                    )}
                  </Space>
                ) },
            ] as ColumnsType<any>}
            scroll={{ x: 1000 }}
          />
        )}
      </Card>

      <Modal
        open={!!detalheRateio} onCancel={() => setDetalheRateio(null)} footer={null}
        title={detalheRateio ? `Detalhe — ${detalheRateio.profissional_nome}` : ''}
        width={780}
      >
        {detalheRateio && (
          <div>
            <Flex gap={20} wrap>
              <Statistic title="Valor origem (lançamento)" value={fmtMoney(detalheRateio.valor_origem)} valueStyle={{ fontSize: 14 }} />
              <Statistic title="Salário base" value={fmtMoney(detalheRateio.valor_esperado)} valueStyle={{ fontSize: 14, color: detalheRateio.divergente ? '#B91626' : undefined }} />
              <Statistic title="Horas total" value={fmtHoras(detalheRateio.horas_total_segundos)} valueStyle={{ fontSize: 14 }} />
            </Flex>
            {detalheRateio.parcelas_no_mes > 1 && (
              <Alert type="warning" showIcon style={{ marginTop: 10 }}
                message={`Profissional pago em ${detalheRateio.parcelas_no_mes} parcelas no mês`}
                description={
                  <div style={{ fontSize: 12 }}>
                    <div>Total do mês: <strong>{fmtMoney(detalheRateio.valor_total_mes)}</strong> (soma de todas as parcelas).</div>
                    <div>Esta parcela específica: <strong>{fmtMoney(detalheRateio.valor_origem)}</strong>.</div>
                    <div style={{ marginTop: 4, color: '#666' }}>As proporções por CC são idênticas em todas as parcelas (usam as mesmas horas). O valor absoluto de cada split é proporcional ao valor da parcela. Aplique todas as parcelas pra ratear o total corretamente.</div>
                  </div>
                } />
            )}
            {detalheRateio.divergente && (
              <Alert type="info" showIcon style={{ marginTop: 10 }}
                message="Total do mês difere do salário base"
                description={`Total do mês: ${fmtMoney(detalheRateio.valor_total_mes || detalheRateio.valor_origem)} vs salário base: ${fmtMoney(detalheRateio.valor_esperado)}. Esperado em meses com bônus/férias/13º. O rateio usa o valor lançado real, distribuído pelas horas. O coeficiente é aplicado depois pela DRE do CC pra projetar custo anualizado.`} />
            )}
            <Title level={5} style={{ marginTop: 16 }}>Splits gerados</Title>
            <Table size="small" rowKey={(_, i) => String(i)} pagination={false}
              dataSource={detalheRateio.splits || []}
              columns={[
                { title: 'Tipo', dataIndex: 'tipo', key: 't', width: 110,
                  render: (v: string) => v === 'projeto'
                    ? <Tag color="blue">CC Projeto</Tag>
                    : <Tag color="orange">Não-projeto</Tag> },
                { title: 'Categoria DRE', dataIndex: 'categoria_dre', key: 'c' },
                { title: 'CC contrato / Projeto Clockify', key: 'cc',
                  render: (_: any, r: any) => {
                    // CC Projeto (com contrato vinculado)
                    if (r.cc_contrato_id) {
                      return (
                        <div>
                          {r.cc_contrato_numero && (
                            <div style={{ fontSize: 11, fontWeight: 500 }}>{r.cc_contrato_numero}</div>
                          )}
                          {r.cc_contrato_titulo && (
                            <div style={{ fontSize: 10, color: '#666' }}>{r.cc_contrato_titulo}</div>
                          )}
                          {!r.cc_contrato_numero && !r.cc_contrato_titulo && (
                            <Text style={{ fontFamily: 'monospace', fontSize: 10 }}>{r.cc_contrato_id.slice(0, 8)}…</Text>
                          )}
                        </div>
                      );
                    }
                    // Não-projeto: mostra o projeto Clockify que gerou as horas
                    if (r.clockify_project_nome) {
                      return (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 500, color: '#fa8c16' }}>
                            {r.clockify_project_nome}
                          </div>
                          <div style={{ fontSize: 10, color: '#888' }}>(projeto Clockify · não-projeto)</div>
                        </div>
                      );
                    }
                    return <Text type="secondary" style={{ fontSize: 10 }}>—</Text>;
                  } },
                { title: 'Horas', dataIndex: 'horas_seg', key: 'h', align: 'right',
                  render: (v: number) => fmtHoras(v) },
                { title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right',
                  render: (v: number) => <strong>{fmtMoney(v)}</strong> },
              ] as ColumnsType<any>}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
